import React from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CustomInput } from "../../../atoms/CustomInput";
import { CustomButton } from "../../../atoms/CustomButton";
import FileDropzone from "../../../atoms/FileDropzone";
import { CustomRegistrationFormData, ProcessDataConfig } from "../types";
import { Label } from "flowbite-react";
import { HiArrowLeft } from "react-icons/hi";
import { CustomQuestion } from "../../dashboard/customRegistration/types";
import { ACCEPTED_FILE_TYPES } from "../../../../constants/fileTypes";
import { CHECKBOX_VALUE_SEPARATOR } from "../utils";

const questionLabelClass = "block text-sm font-medium text-gray-700";

const FILE_ACCEPT_RECORD: Record<string, string[]> = {
  "*/*": ACCEPTED_FILE_TYPES.split(","),
};

interface Step3CustomQuestionsProps {
  config: ProcessDataConfig;
  control: Control<CustomRegistrationFormData> | null;
  errors: FieldErrors<CustomRegistrationFormData> | null;
  isSubmitting: boolean;
  onBack?: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isLastStep?: boolean;
  isPreview?: boolean;
}

const RenderQuestionField: React.FC<{
  question: CustomQuestion;
  index: number;
  control: Control<CustomRegistrationFormData> | null;
  errors: FieldErrors<CustomRegistrationFormData> | null;
  isSubmitting: boolean;
  isPreview: boolean;
}> = ({ question, index, control, errors, isSubmitting, isPreview }) => {
  const { t } = useTranslation("clientView");
  const fieldName = `question_${index}`;
  const fieldKey = fieldName as keyof CustomRegistrationFormData;
  const fieldType = question.fieldType || "text";
  const isRequired = question.required !== false;

  const label = `${question.question}${isRequired ? " *" : ""}`;
  const isDisabled = isSubmitting || isPreview;

  switch (fieldType) {
    case "textarea":
      return (
        <div key={index} className="space-y-2 my-10">
          <Label htmlFor={fieldName} className={questionLabelClass}>
            {label}
          </Label>
          <CustomInput
            maxLength={1000}
            name={fieldName}
            control={control!}
            placeholder={
              t("customRegistration.page.step3.answerPlaceholder") as string
            }
            rows={3}
            error={errors?.[fieldKey]}
            helperText={errors?.[fieldKey]}
            disabled={isDisabled}
            aria-required={isRequired}
            aria-invalid={!!errors?.[fieldKey]}
            aria-describedby={
              errors?.[fieldKey] ? `${fieldName}-error` : undefined
            }
          />
          {!isPreview && control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <p className="text-xs text-gray-500 text-end mt-1">
                  {(typeof field.value === "string" ? field.value : "").length}
                  /1000
                </p>
              )}
            />
          )}
        </div>
      );

    case "number":
      return (
        <div key={index} className="space-y-2 my-10">
          <Label htmlFor={fieldName} className={questionLabelClass}>
            {label}
          </Label>
          {control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="number"
                  id={fieldName}
                  disabled={isDisabled}
                  aria-required={isRequired}
                  aria-invalid={!!errors?.[fieldKey]}
                  aria-describedby={
                    errors?.[fieldKey] ? `${fieldName}-error` : undefined
                  }
                  onChange={(e) => field.onChange(e.target.value)}
                  value={typeof field.value === "string" ? field.value : ""}
                  className={`w-full p-2.5 md:p-2 text-base md:text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    errors?.[fieldKey] ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={
                    t(
                      "customRegistration.page.step3.answerPlaceholder"
                    ) as string
                  }
                />
              )}
            />
          )}
          {errors?.[fieldKey] && !isPreview && (
            <p
              id={`${fieldName}-error`}
              className="text-red-600 text-xs"
              role="alert"
            >
              {errors[fieldKey]?.message}
            </p>
          )}
        </div>
      );

    case "date":
    case "datetime-local":
    case "time":
      return (
        <div key={index} className="space-y-2 my-10">
          <Label htmlFor={fieldName} className={questionLabelClass}>
            {label}
          </Label>
          {control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type={fieldType}
                  id={fieldName}
                  disabled={isDisabled}
                  aria-required={isRequired}
                  aria-invalid={!!errors?.[fieldKey]}
                  aria-describedby={
                    errors?.[fieldKey] ? `${fieldName}-error` : undefined
                  }
                  onChange={(e) => field.onChange(e.target.value)}
                  value={typeof field.value === "string" ? field.value : ""}
                  className={`w-full p-2.5 md:p-2 text-base md:text-sm border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    errors?.[fieldKey] ? "border-red-500" : "border-gray-300"
                  }`}
                />
              )}
            />
          )}
          {errors?.[fieldKey] && !isPreview && (
            <p
              id={`${fieldName}-error`}
              className="text-red-600 text-xs"
              role="alert"
            >
              {errors[fieldKey]?.message}
            </p>
          )}
        </div>
      );

    case "file":
      return (
        <div key={index} className="space-y-2 my-10">
          <Label htmlFor={fieldName} className={questionLabelClass}>
            {label}
          </Label>
          {control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field: { onChange, value } }) => (
                <FileDropzone
                  labels={{
                    choose: t("customRegistration.page.step3.chooseFile"),
                    none: t("customRegistration.page.step3.noFileChosen"),
                    remove: t("signing.removeUpload"),
                  }}
                  selectedFile={value instanceof File ? value : null}
                  displayName={typeof value === "string" ? value : undefined}
                  onFileSelected={(file) => onChange(file)}
                  onFileRemoved={() => onChange(null)}
                  accept={FILE_ACCEPT_RECORD}
                  maxSizeMB={5}
                  fileTooLargeMessage={
                    t("customRegistration.page.step3.fileTooLarge") as string
                  }
                  invalidTypeMessage={
                    t("customRegistration.page.step3.invalidFileType") as string
                  }
                  hint={
                    !isPreview
                      ? (t("customRegistration.page.step3.maxFileSize", {
                          size: "5 MB",
                        }) as string)
                      : undefined
                  }
                  disabled={isDisabled}
                  hasError={!!errors?.[fieldKey] && !isPreview}
                  errorMessage={errors?.[fieldKey]?.message}
                />
              )}
            />
          )}
        </div>
      );

    case "radio":
      return (
        <div key={index} className="space-y-2 my-10">
          <Label className={questionLabelClass}>{label}</Label>
          {control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <div
                  role="radiogroup"
                  aria-required={isRequired}
                  aria-invalid={!!errors?.[fieldKey]}
                  aria-describedby={
                    errors?.[fieldKey] ? `${fieldName}-error` : undefined
                  }
                  className="space-y-2"
                >
                  {question.options?.map((option: string, optIdx: number) => (
                    <div key={optIdx} className="flex items-center">
                      <input
                        type="radio"
                        id={`${fieldName}_${optIdx}`}
                        name={fieldName}
                        value={String(option)}
                        checked={field.value === String(option)}
                        onChange={(e) => field.onChange(e.target.value)}
                        disabled={isDisabled}
                        className="w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label
                        htmlFor={`${fieldName}_${optIdx}`}
                        className={`ms-3 text-gray-600 text-sm flex-1 py-2 ${
                          isDisabled
                            ? "cursor-not-allowed opacity-50"
                            : "cursor-pointer"
                        }`}
                      >
                        {String(option)}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            />
          )}
          {errors?.[fieldKey] && !isPreview && (
            <p
              id={`${fieldName}-error`}
              className="text-red-600 text-xs"
              role="alert"
            >
              {errors[fieldKey]?.message}
            </p>
          )}
        </div>
      );

    case "checkbox":
      return (
        <div key={index} className="space-y-2 my-10">
          <Label className={questionLabelClass}>{label}</Label>
          {control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => {
                const values =
                  typeof field.value === "string"
                    ? field.value
                        .split(CHECKBOX_VALUE_SEPARATOR)
                        .filter(Boolean)
                    : [];

                return (
                  <div
                    role="group"
                    aria-labelledby={`${fieldName}-label`}
                    aria-describedby={
                      errors?.[fieldKey] ? `${fieldName}-error` : undefined
                    }
                    className="space-y-2"
                  >
                    {question.options?.map((option: string, optIdx: number) => (
                      <div key={optIdx} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`${fieldName}_${optIdx}`}
                          name={`${fieldName}_${optIdx}`}
                          value={String(option)}
                          checked={values.includes(String(option))}
                          disabled={isDisabled}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field.onChange(
                                [...values, option].join(
                                  CHECKBOX_VALUE_SEPARATOR
                                )
                              );
                            } else {
                              field.onChange(
                                values
                                  .filter((v) => v !== String(option))
                                  .join(CHECKBOX_VALUE_SEPARATOR)
                              );
                            }
                          }}
                          className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <label
                          htmlFor={`${fieldName}_${optIdx}`}
                          className={`ms-3 text-gray-600 text-sm flex-1 py-2 ${
                            isDisabled
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                          }`}
                        >
                          {String(option)}
                        </label>
                      </div>
                    ))}
                  </div>
                );
              }}
            />
          )}
          {errors?.[fieldKey] && !isPreview && (
            <p
              id={`${fieldName}-error`}
              className="text-red-600 text-xs"
              role="alert"
            >
              {errors[fieldKey]?.message}
            </p>
          )}
        </div>
      );

    case "text":
    default:
      return (
        <div key={index} className="space-y-2 my-10">
          <Label htmlFor={fieldName} className={questionLabelClass}>
            {label}
          </Label>
          <CustomInput
            maxLength={500}
            name={fieldName}
            control={control!}
            placeholder={
              t("customRegistration.page.step3.answerPlaceholder") as string
            }
            rows={1}
            error={errors?.[fieldKey]}
            helperText={errors?.[fieldKey]}
            disabled={isDisabled}
            aria-required={isRequired}
            aria-invalid={!!errors?.[fieldKey]}
            aria-describedby={
              errors?.[fieldKey] ? `${fieldName}-error` : undefined
            }
          />
          {!isPreview && control && (
            <Controller
              name={fieldName}
              control={control}
              render={({ field }) => (
                <p className="text-xs text-gray-500 text-end mt-1">
                  {(typeof field.value === "string" ? field.value : "").length}
                  /500
                </p>
              )}
            />
          )}
        </div>
      );
  }
};

export const Step3CustomQuestions: React.FC<Step3CustomQuestionsProps> = ({
  config,
  control,
  errors,
  isSubmitting,
  onBack,
  onSubmit,
  isLastStep = true,
  isPreview = false,
}) => {
  const { t } = useTranslation("clientView");
  const shouldUsePreview = isPreview || control === null;

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {!shouldUsePreview && onBack && (
        <CustomButton
          variant="none"
          size="xs"
          onClick={onBack}
          icon={<HiArrowLeft size={16} className="rtl:rotate-180" />}
          title={t("customRegistration.page.step3.backButton")}
          className="mb-4 justify-start px-0 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        />
      )}

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t("customRegistration.page.step3.title")}
        </h2>
        <p className="text-gray-600">
          {t("customRegistration.page.step3.description")}
        </p>
      </div>

      <div className="space-y-6">
        {config.customQuestions.map((question, index) => (
          <div key={index} className="mb-6">
            <RenderQuestionField
              question={question}
              index={index}
              control={control}
              errors={errors}
              isSubmitting={isSubmitting}
              isPreview={shouldUsePreview}
            />
          </div>
        ))}
      </div>

      {!shouldUsePreview && (
        <div className="pt-4">
          <CustomButton
            variant="primary"
            type="submit"
            title={
              isSubmitting
                ? isLastStep
                  ? t("customRegistration.page.step3.submitting")
                  : t("customRegistration.page.step2.processing")
                : isLastStep
                ? t("customRegistration.page.step3.submitButton")
                : t("customRegistration.page.step2.nextButton")
            }
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          />
        </div>
      )}
    </form>
  );
};
